// Generic renderer for the `figure` render_spec kind.
//
// This is a MECHANICAL MAP from extracted primitives to SVG elements -- a line
// in the source PDF becomes an <line>, a path becomes a <polyline>, a diagram
// label becomes <text>. There is no per-diagram drawing code and no artistic
// decision anywhere in here; the geometry is whatever the extractor read off the
// page. That is the point: the diagram is reconstructed FROM THE PAPER rather
// than redrawn from an interpretation of it.
//
// The corpus has two figure regimes and this handles both:
//   * vector papers (2024-era) carry real line/curve objects -> `elements`
//   * the 2007-2022 compendiums have no vector layer at all, only embedded
//     bitmaps -> `image_key`, served from the academy assets route.
//
// A bitmap is (c) Australian Maths Trust and reference-only. It exists so a
// moderator can see the figure; it is not product content.
import type { MathSpec } from './academyApi';
import { AcademyAssetImage } from './AcademyAssetImage';
import { ACADEMY_ICONS } from './academyFigureIcons';

export type FigureElement =
  | {
      t: 'line';
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      arrow?: boolean;
      stroke?: Fill;
    }
  | {
      t: 'polyline';
      points: Array<[number, number]>;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      fill?: Fill;
      stroke?: Fill;
    }
  | {
      t: 'polygon';
      points: Array<[number, number]>;
      fill?: Fill;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      stroke?: Fill;
    }
  | {
      t: 'circle';
      cx: number;
      cy: number;
      r: number;
      fill?: Fill;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      stroke?: Fill;
    }
  // Perspective circles (wheels, cylinder rims) are common; without this every
  // one needed a polygon sampled around the ellipse.
  | {
      t: 'ellipse';
      cx: number;
      cy: number;
      rx: number;
      ry: number;
      fill?: Fill;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      stroke?: Fill;
    }
  | {
      t: 'rect';
      x: number;
      y: number;
      width: number;
      height: number;
      fill?: Fill;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      stroke?: Fill;
    }
  /**
   * A SMOOTH curve through the given points (Catmull-Rom, so the curve passes
   * through every point rather than being pulled towards it). Papers are full of
   * freehand outlines -- a cross-country course, a scalloped cut, a coastline --
   * and sampling one as a polygon shows its corners at any useful size.
   *
   * Caveat worth keeping in mind: when a question asks the reader to ESTIMATE an
   * area from a freehand blob, the curve you choose IS the answer. This primitive
   * makes such a figure drawable, not decidable; those stay blocked on judgement.
   */
  | {
      t: 'curve';
      points: Array<[number, number]>;
      closed?: boolean;
      fill?: Fill;
      dashed?: boolean;
      dotted?: boolean;
      w?: number;
      stroke?: Fill;
    }
  | {
      t: 'arc';
      cx: number;
      cy: number;
      r: number;
      start: number;
      end: number;
      w?: number;
      dashed?: boolean;
      dotted?: boolean;
      stroke?: Fill;
      arrow?: boolean;
    }
  | {
      t: 'label';
      x: number;
      y: number;
      text: string;
      anchor?: 'start' | 'middle' | 'end';
      /** Degrees clockwise about (x, y) — letters on a rotated or isometric face. */
      rotate?: number;
      /**
       * Reflect about the vertical through (x, y). A mirrored letter is NOT a
       * rotated one, and several questions turn on telling the two apart (the
       * KATE bead necklace is reprinted across three divisions and was blocked
       * in all three purely for want of this).
       */
      mirror?: boolean;
      /**
       * Ink colour. A label and a `solid` fill both default to currentColor, so
       * a numeral on a filled cell was invisible -- and papers really do print
       * white numerals on black squares (a numbered checkerboard).
       */
      color?: Fill;
      /**
       * Papers set variable letters in italic, and stem_text/options can already
       * say so via \textit{}. Without this the SAME letter renders upright in
       * the diagram and italic in the sentence beside it.
       */
      italic?: boolean;
      /**
       * Text size as a multiple of the default (view_box_width / 16). The
       * default is a fixed fraction of the frame, which makes "one big letter
       * filling a tile" impossible to draw at printed proportions -- the
       * workaround was an 8x oversized view_box with invisible margins.
       */
      size?: number;
    }
  /**
   * One of our own drawn marks (see academyFigureIcons), placed INSIDE the
   * figure. Needed because several questions are built on artwork sitting at a
   * measured position within surrounding geometry -- a picture in a spinner
   * sector, a symbol in a map cell -- which `image_key` cannot express, since
   * it replaces the entire figure with a single raster.
   */
  | {
      t: 'icon';
      /** A key of ACADEMY_ICONS. An unknown key draws nothing. */
      key: string;
      /** CENTRE of the mark, not its corner -- these sit in cells and sectors. */
      x: number;
      y: number;
      /** Width and height of the (square) mark, in view_box units. */
      size: number;
      /** Degrees clockwise about (x, y). */
      rotate?: number;
      /** Reflect about the vertical through (x, y). A mirrored mark is not a rotated one. */
      mirror?: boolean;
    };

// A diagram's meaning often rides on fill -- which cell is shaded, which piece is
// painted. Rather than let a transcriber pick colours (that would be drawing, not
// describing), fills are a small named set mapped to design-system tones here.
export type Fill =
  | 'none'
  | 'solid'
  | 'light'
  | 'hatch'
  // Named, not free: several questions turn on WHICH colour a region is
  // ("the red face is opposite the blue face"), which no amount of grey can
  // express. The transcriber names the paper's colour; the palette is ours.
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'orange'
  | 'purple'
  | 'pink'
  // Opaque, so a shape drawn later HIDES what it overlaps. Overlap order is the
  // whole question in "which card is on top"; without this it cannot be drawn.
  | 'white'
  // Opaque mid-grey. `light` is 12% black AND semi-transparent; `solid` is full
  // ink. Neither matches the mid-grey AMC prints for a recessed opening, so
  // transcribers were reaching for `solid` — drawing a black hole — as the
  // least-wrong option available.
  | 'grey';

export type FigureSpec = {
  kind: 'figure';
  view_box?: [number, number, number, number];
  elements?: FigureElement[];
  image_key?: string;
  /**
   * Per-option artwork, index-aligned with AcademyQuestion.options. AMC papers
   * reliably carry 1-3 "which picture" questions each; without this they cannot
   * be represented at all, and describing five drawings in words rewrites the
   * question instead of transcribing it.
   */
  option_specs?: Array<Omit<FigureSpec, 'option_specs' | 'choices' | 'latex' | 'kind'>>;
} & Partial<Pick<MathSpec, 'latex' | 'choices'>>;

/**
 * Bounding box of every element, so `view_box` never has to be guessed.
 *
 * Labels contribute their TEXT EXTENT, not just their anchor point. Fitting to
 * anchors alone still clipped silently -- a right-anchored "10 m" or a rotated
 * axis title sitting at the edge simply disappeared, which is the exact failure
 * auto-fitting was meant to remove. Font size depends on box width and the box
 * depends on the text, so the estimate is iterated to a fixed point.
 */
function fitBox(elements: FigureElement[]): [number, number, number, number] | null {
  const xs: number[] = [];
  const ys: number[] = [];
  const add = (x: number, y: number) => {
    xs.push(x);
    ys.push(y);
  };
  for (const el of elements) {
    if (el.t === 'line') {
      add(el.x1, el.y1);
      add(el.x2, el.y2);
    } else if (el.t === 'polyline' || el.t === 'polygon') {
      el.points.forEach((p) => add(p[0], p[1]));
    } else if (el.t === 'circle') {
      add(el.cx - el.r, el.cy - el.r);
      add(el.cx + el.r, el.cy + el.r);
    } else if (el.t === 'ellipse') {
      add(el.cx - el.rx, el.cy - el.ry);
      add(el.cx + el.rx, el.cy + el.ry);
    } else if (el.t === 'rect') {
      add(el.x, el.y);
      add(el.x + el.width, el.y + el.height);
    } else if (el.t === 'curve') {
      // Control points of a Catmull-Rom stay within the hull of its points, so
      // the point bbox bounds the drawn curve closely enough to fit to.
      el.points.forEach((p) => add(p[0], p[1]));
    } else if (el.t === 'icon') {
      add(el.x - el.size / 2, el.y - el.size / 2);
      add(el.x + el.size / 2, el.y + el.size / 2);
    } else if (el.t === 'arc') {
      // The SWEPT extent, not the whole circle: a 30-degree dial arc bounded by
      // its full circle pads the figure out to four times its real size, which
      // then shrinks every label (fontSize scales with box width).
      const rad = (d: number) => (d * Math.PI) / 180;
      const lo = Math.min(el.start, el.end);
      const hi = Math.max(el.start, el.end);
      add(el.cx + el.r * Math.cos(rad(el.start)), el.cy + el.r * Math.sin(rad(el.start)));
      add(el.cx + el.r * Math.cos(rad(el.end)), el.cy + el.r * Math.sin(rad(el.end)));
      // An axis extreme only counts when the sweep actually crosses it.
      for (let a = Math.ceil(lo / 90) * 90; a <= hi; a += 90) {
        add(el.cx + el.r * Math.cos(rad(a)), el.cy + el.r * Math.sin(rad(a)));
      }
    }
  }
  if (!xs.length && !elements.some((e) => e.t === 'label')) return null;

  const texts = elements.filter(
    (e): e is Extract<FigureElement, { t: 'label' }> => e.t === 'label',
  );

  const bounds = () => {
    const bx0 = Math.min(...xs);
    const by0 = Math.min(...ys);
    return [bx0, by0, Math.max(...xs) - bx0 || 1, Math.max(...ys) - by0 || 1] as const;
  };
  if (!xs.length) texts.forEach((t) => add(t.x, t.y));

  // Two rounds converge: width -> font size -> text extent -> width.
  for (let pass = 0; pass < 2; pass += 1) {
    const [, , bw] = bounds();
    // NO floor: view_box units are arbitrary, so a floor measured in them is
    // scale-dependent in a scale-invariant space. A floor of 5 meant any figure
    // under ~80 units wide silently switched to a DIFFERENT, larger label size
    // than the one the box was fitted for, and clipped ("Tascott" -> "ott").
    const fs = bw / 16;
    for (const t of texts) {
      // 0.58em average advance is close enough for a bounding estimate.
      const tw = t.text.length * 0.58 * fs * (t.size ?? 1);
      const anchor = t.anchor ?? 'start';
      const left = anchor === 'end' ? t.x - tw : anchor === 'middle' ? t.x - tw / 2 : t.x;
      const rot = t.rotate ?? 0;
      const fsz = fs * (t.size ?? 1);
      if (rot % 180 === 0) {
        add(left, t.y - fsz);
        add(left + tw, t.y + fsz * 0.25);
      } else {
        // Rotated text occupies the other axis; be generous rather than clip.
        add(t.x - fsz, t.y - tw);
        add(t.x + fsz, t.y + tw);
      }
    }
  }
  const [x0, y0, w, h] = bounds();
  return [x0, y0, w, h];
}


/**
 * Catmull-Rom through `pts` as a cubic bezier `d`. The curve interpolates every
 * point, which is what a transcriber needs: the points are places the outline
 * was measured to pass through, not pull-handles.
 */
function smoothPath(pts: Array<[number, number]>, closed?: boolean): string {
  if (pts.length < 2) return '';
  const at = (i: number): [number, number] =>
    closed
      ? pts[((i % pts.length) + pts.length) % pts.length]
      : pts[Math.max(0, Math.min(pts.length - 1, i))];
  const last = closed ? pts.length : pts.length - 1;
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < last; i += 1) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return closed ? `${d} Z` : d;
}

export function AcademyFigure({ spec }: { spec: FigureSpec }) {
  if (spec.image_key) {
    return (
      <AcademyAssetImage
        data-testid="academy-figure-raster"
        assetKey={spec.image_key}
        alt="Figure from the source paper"
        className="mt-2 max-w-full rounded-lg border border-line"
        loading="lazy"
      />
    );
  }

  const elements = spec.elements ?? [];
  if (elements.length === 0) return null;
  // A hand-authored view_box clips silently -- geometry outside it simply
  // vanishes with no error, which cost three isometric figures in one paper.
  // Derive it from the elements unless one is given.
  const box = spec.view_box ?? fitBox(elements);
  if (!box) return null;
  const [, , w, h] = box;
  if (!(w > 0 && h > 0)) return null;

  // Both of these are FRACTIONS OF THE BOX, never absolute amounts. view_box
  // units carry no scale, so a floor expressed in them means a figure drawn in
  // small numbers gets fat strokes and a huge margin while the identical figure
  // drawn in large numbers does not -- the same defect the label size had. At
  // view_box width 35 the old floors spent ~17% of the frame per side on margin
  // and drew every line 4x too heavy.
  const stroke = w / 260;
  // Enough to keep a stroke centred on the boundary from being half cut off,
  // and a little air beyond that.
  const pad = Math.max(stroke, Math.min(w, h) * 0.08);

  const NAMED: Record<string, string> = {
    red: '#e5484d',
    blue: '#3b82f6',
    green: '#30a46c',
    yellow: '#f5d90a',
    orange: '#f76b15',
    purple: '#8e4ec6',
    // AMC prints pink often enough (a shaded region, a slice, a run of dots)
    // that transcribers were each deciding on their own whether to call it red
    // -- which collides whenever the same figure also prints a real red.
    pink: '#e93d82',
    white: 'var(--card, #fff)',
    grey: '#9aa0a6',
  };
  const fillOf = (f?: Fill) =>
    f === 'solid'
      ? 'currentColor'
      : f === 'light'
        ? 'rgba(0,0,0,.12)'
        : f === 'hatch'
          ? 'url(#hatch)'
          : f && NAMED[f]
            ? NAMED[f]
            : 'none';
  const dash = (el: { dashed?: boolean; dotted?: boolean }) =>
    el.dotted ? `${stroke} ${stroke * 2}` : el.dashed ? `${stroke * 3} ${stroke * 2}` : undefined;
  const pts = (p: Array<[number, number]>) => p.map((q) => q.join(',')).join(' ');
  // Arc endpoints from centre/radius/angles, so a transcriber gives geometry
  // rather than hand-authoring a path string.
  const arcPath = (cx: number, cy: number, r: number, a0: number, a1: number) => {
    const rad = (d: number) => (d * Math.PI) / 180;
    const x0 = cx + r * Math.cos(rad(a0));
    const y0 = cy + r * Math.sin(rad(a0));
    const x1 = cx + r * Math.cos(rad(a1));
    const y1 = cy + r * Math.sin(rad(a1));
    const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
    const sweep = a1 > a0 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
  };

  return (
    <svg
      data-testid="academy-figure"
      viewBox={`${box[0] - pad} ${box[1] - pad} ${w + pad * 2} ${h + pad * 2}`}
      className="mt-2 h-auto w-full max-w-[420px]"
      role="img"
      aria-label="Diagram reconstructed from the source paper"
    >
      <defs>
        <marker
          id="arrowhead"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          {/*
            * context-stroke, NOT currentColor. Marker content inherits from the
            * marker's own ancestors in <defs>, never from the element that
            * references it, so `currentColor` here always resolved to the page
            * ink -- a red-stroked arrow rendered with a black arrowhead. Papers
            * draw coloured arrows (a fold direction, a move) and a two-tone one
            * looks like a mistake. Browsers without context-stroke fall back to
            * black, i.e. exactly the old behaviour.
            */}
          <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
        </marker>
        <pattern
          id="hatch"
          width="4"
          height="4"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="4" stroke="currentColor" strokeWidth="1" />
        </pattern>
      </defs>
      <g stroke="currentColor" strokeLinejoin="round" strokeLinecap="round">
        {elements.map((el, i) => {
          const strokeColour = (el as { stroke?: Fill }).stroke;
          // `key` is deliberately NOT in here: React strips a spread `key` and
          // warns, and React 19 drops it outright, which would silently break
          // reconciliation for every primitive in the figure.
          const common = {
            // `light` resolves here as well as in fills: a faint grid or a
            // construction line is a real idiom, and without this the value
            // silently fell through to the default ink with no error at all.
            stroke: strokeColour
              ? strokeColour === 'light'
                ? 'rgba(0,0,0,.12)'
                : (NAMED[strokeColour] ?? undefined)
              : undefined,
            strokeWidth: (el as { w?: number }).w ? stroke * (el as { w?: number }).w! : stroke,
            strokeDasharray: dash(el as { dashed?: boolean; dotted?: boolean }),
          };
          if (el.t === 'curve') {
            return (
              <path
                {...common}
                key={i}
                d={smoothPath(el.points, el.closed)}
                fill={fillOf(el.fill)}
              />
            );
          }
          if (el.t === 'line') {
            return (
              <line
                {...common}
                key={i}
                x1={el.x1}
                y1={el.y1}
                x2={el.x2}
                y2={el.y2}
                fill="none"
                // Dimension lines and axes are a constant AMC idiom; without a
                // marker every one lost its arrowheads.
                markerEnd={el.arrow ? 'url(#arrowhead)' : undefined}
              />
            );
          }
          if (el.t === 'polyline') {
            return <polyline {...common} key={i} points={pts(el.points)} fill={fillOf(el.fill)} />;
          }
          if (el.t === 'polygon') {
            return <polygon {...common} key={i} points={pts(el.points)} fill={fillOf(el.fill)} />;
          }
          if (el.t === 'circle') {
            return (
              <circle {...common} key={i} cx={el.cx} cy={el.cy} r={el.r} fill={fillOf(el.fill)} />
            );
          }
          if (el.t === 'ellipse') {
            return (
              <ellipse
                {...common}
                key={i}
                cx={el.cx}
                cy={el.cy}
                rx={el.rx}
                ry={el.ry}
                fill={fillOf(el.fill)}
              />
            );
          }
          if (el.t === 'rect') {
            return (
              <rect
                {...common}
                key={i}
                x={el.x}
                y={el.y}
                width={el.width}
                height={el.height}
                fill={fillOf(el.fill)}
              />
            );
          }
          if (el.t === 'arc') {
            return (
              <path
                {...common}
                key={i}
                d={arcPath(el.cx, el.cy, el.r, el.start, el.end)}
                fill="none"
                markerEnd={el.arrow ? 'url(#arrowhead)' : undefined}
              />
            );
          }
          if (el.t === 'icon') {
            const ic = ACADEMY_ICONS[el.key];
            // An unknown key draws nothing rather than throwing: a bad key must
            // not blank the whole question for a kid mid-session.
            if (!ic) return null;
            const [vx, vy, vw, vh] = ic.viewBox;
            const k = el.size / Math.max(vw, vh);
            return (
              <g
                key={i}
                transform={[
                  el.rotate ? `rotate(${el.rotate} ${el.x} ${el.y})` : '',
                  // Mirror about the vertical through the CENTRE, so a
                  // reflected mark stays in its cell instead of jumping.
                  // Same order caveat as `label`: later in the list means
                  // applied FIRST, so this renders rotate(mirror(mark)). To
                  // express mirror-after-rotate by t, author rotate(-t).
                  el.mirror ? `translate(${el.x * 2} 0) scale(-1 1)` : '',
                  `translate(${el.x - (vw * k) / 2} ${el.y - (vh * k) / 2})`,
                  `scale(${k})`,
                  `translate(${-vx} ${-vy})`,
                ]
                  .filter(Boolean)
                  .join(' ')}
                // The mark is authored as standalone SVG stroked in
                // currentColor; inlining it here is what lets it inherit the
                // figure's ink (and the dark-theme flip) and keeps the figure
                // network-free so the offline harness can rasterise it.
                dangerouslySetInnerHTML={{ __html: ic.inner }}
              />
            );
          }
          return null;
        })}
      </g>
      {elements.map((el, i) =>
        el.t === 'label' ? (
          <text
            key={`t${i}`}
            x={el.x}
            y={el.y}
            fontSize={(w / 16) * (el.size ?? 1)}
            textAnchor={el.anchor ?? 'start'}
            transform={
              [
                el.rotate ? `rotate(${el.rotate} ${el.x} ${el.y})` : '',
                // Translate-flip-translate keeps the anchor fixed, so a mirrored
                // label stays where it was placed instead of jumping across the
                // figure.
                //
                // ORDER: it sits after `rotate` in the transform LIST, which in
                // SVG means it is applied to the glyph FIRST -- the rendered
                // result is rotate(mirror(glyph)), not mirror(rotate(glyph)).
                // The two differ for every angle that is not a multiple of 180,
                // and getting it backwards silently produces a valid-looking but
                // WRONG picture, which decides the answer in any question about
                // reflections. To express mirror-after-rotate by angle t, author
                // it as rotate(-t) with mirror, since M.R(t) = R(-t).M.
                el.mirror ? `translate(${el.x * 2} 0) scale(-1 1)` : '',
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            fontStyle={el.italic ? 'italic' : undefined}
            fill={el.color ? (fillOf(el.color) === 'none' ? 'currentColor' : fillOf(el.color)) : 'currentColor'}
            stroke="none"
          >
            {el.text}
          </text>
        ) : null,
      )}
    </svg>
  );
}
